import { ImageTransformOptions, ImageTransformType } from '@diritto/common/imageKit/constants';
import { SectionCustomQuickMenuDto } from '@diritto/common/relay/dtos/section-relay.dtos';
import { TrackerExtraInfoProps, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { setUrlQueryString } from '@diritto/common/utils/urls';
import styled from '@emotion/styled';
import { SectionSlideButton } from 'app-components/home/sections/components/SectionSlideButton';
import {
  Style,
  StyledBackgroundImage,
  StyledItemWrapper,
  StyledTextWrapper,
  StyledTitle,
} from 'app-components/home/sections/CustomQuickMenuStyle';
import { isPC } from 'constants/layout';
import { useAuthState } from 'contexts/AuthProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import { DateTime } from 'luxon';
import React, { ReactNode, useCallback, useMemo, useRef } from 'react';
import { hideScrollbar } from 'styles/common';
import { ExternalLink } from 'ui-components/common/ExternalLink';
import { Link } from 'ui-components/common/Link';
import { useSlide } from 'ui-hooks/useSlide';

const HorizontalGap = 16;

export const SectionCustomQuickMenu = ({
  section,
  trackerExtraInfo,
}: { section: SectionCustomQuickMenuDto } & TrackerExtraInfoProps) => {
  const { profile } = useAuthState();
  const { tracker } = useTrackingState();
  const slideRef = useRef<HTMLDivElement>(null);
  const { handlePrevSlide, handleNextSlide, handleSlide, showSlideButton } = useSlide(slideRef, HorizontalGap);
  const style = {
    height: 64,
    width: 64,
    description: '',
    title: '',
  };

  const handleClick = useCallback(
    (index: number) => {
      tracker.track(TrackingEventType.ClickItemOnHomeSection, {
        sectionId: section.id,
        sectionDesign: section.design,
        listItemIndex: index,
        trackerExtraInfo,
      });
      tracker.track(TrackingEventType.ClickSectionCustomQuickMenu);
    },
    [section.design, section.id, tracker, trackerExtraInfo],
  );

  const getUrl = useCallback(
    (link: string) =>
      link.includes('typeform.com')
        ? setUrlQueryString(link, 'profile_id', profile?.id ? profile.id.toString() : '')
        : link,
    [profile?.id],
  );

  const filteredItems = useMemo(() => {
    const now = DateTime.now();
    return section.items.filter(
      item => now >= DateTime.fromISO(`${item.startAt}`) && now <= DateTime.fromISO(`${item.endAt}`),
    );
  }, [section.items]);

  if (filteredItems.length === 0) {
    return null;
  }

  return (
    <StyledWrapper size={style}>
      <SectionSlideButton
        showSlideButton={showSlideButton}
        handleNextSlide={handleNextSlide}
        handlePrevSlide={handlePrevSlide}
      />
      <StyledScrollContainer ref={slideRef} onScroll={handleSlide}>
        <StyledScrollContainerInner>
          {filteredItems.map((item, index) => (
            <StyledItemWrapper key={`${section.title}_${item.file.id}_${item.title}`} itemStyle={style}>
              <ItemWrapper
                key={`${item.file}-${item.title}`}
                isExternal={item.isExternal}
                url={getUrl(item.link)}
                onClick={() => handleClick(index)}>
                <StyledBackgroundImage
                  itemStyle={style}
                  transform={{
                    path: item.file.path,
                    isPrivate: item.file.isPrivate,
                    transformation: ImageTransformOptions[ImageTransformType.Banner],
                  }}
                />
                <StyledTextWrapper>
                  <StyledTitle>{item.title}</StyledTitle>
                </StyledTextWrapper>
              </ItemWrapper>
            </StyledItemWrapper>
          ))}
        </StyledScrollContainerInner>
      </StyledScrollContainer>
    </StyledWrapper>
  );
};

const ItemWrapper = ({
  children,
  isExternal,
  onClick,
  url,
}: {
  isExternal: boolean;
  children: ReactNode;
  onClick?: any;
  url: string;
}) =>
  isExternal ? (
    <ExternalLink href={url} onClick={onClick}>
      {children}
    </ExternalLink>
  ) : (
    <Link href={url} onClick={onClick}>
      {children}
    </Link>
  );

const StyledWrapper = styled.div<{ size: Style }>`
  position: relative;
  display: flex;
  flex-direction: row;
  height: ${props => props.size.height}px;
  margin-bottom: 32px;
`;

const StyledScrollContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex-flow: column wrap;
  align-content: flex-start;
  width: fit-content;
  margin: 0 auto;
  padding: 0 16px;
  overflow: hidden;
  ${hideScrollbar};
  height: 92px;
  overflow-x: scroll;
  scroll-snap-type: x mandatory;

  @media (${isPC}) {
    padding: 0;
    margin: 0 15px;
    width: 700px;
    scroll-snap-type: x;
  }
`;

const StyledScrollContainerInner = styled.div`
  display: flex;
  flex-direction: row;
  min-width: 100%;
  gap: ${HorizontalGap}px;
  justify-content: center;
`;
